import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { BIKES, PLACES } from "@/components/masar/media";
import ChapterMark from "@/components/masar/ChapterMark";
import StoreBadges from "@/components/masar/StoreBadges";
import { useLanguage } from "@/i18n/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

const plates = [
  { src: PLACES.egyptPyramids, city: "Cairo", region: "Egypt / EG" },
  { src: PLACES.desertDunes, city: "Marrakech", region: "Atlas / MA" },
  { src: PLACES.desertWadi, city: "Petra", region: "Wadi Rum / JO" },
  { src: PLACES.dubaiSkyline, city: "Dubai", region: "The Gulf / AE" },
  { src: BIKES.classicCruiser, city: "Istanbul", region: "Cappadocia / TR" },
];

export default function CinematicBand() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="masar-section-cinematic"
      className="relative overflow-hidden bg-black"
    >
      {/* Parallax desert canvas with slow cinematic drift */}
      <div className="relative">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <motion.div
            className="absolute -inset-[6%] bg-cover bg-center"
            style={{ backgroundImage: `url(${PLACES.desertWadi})` }}
            animate={
              reduceMotion
                ? undefined
                : {
                    scale: [1, 1.07, 1.03, 1],
                    x: ["0%", "-1.5%", "1%", "0%"],
                    y: ["0%", "-1%", "0.5%", "0%"],
                  }
            }
            transition={{ duration: 36, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black" />

        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 py-28 sm:py-40">
          <ChapterMark n={1} label={t("territory.chapter")} />

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 1, ease: EASE }}
            data-testid="masar-hero-territory-headline"
            className="mt-8 font-serif text-white text-5xl sm:text-7xl lg:text-[6.5rem] leading-[0.94] tracking-[-0.03em] font-light max-w-5xl"
          >
            {t("territory.h1a")}
            <br />
            {t("territory.h1to")}{" "}
            <span className="font-serif-italic text-[#FF6A1A]">
              {t("territory.h1b")}
            </span>{" "}
            <span className="font-serif-italic text-[#FF6A1A]">
              {t("territory.h1c")}
            </span>
            <br />
            {t("territory.h1d")}
            <br />
            <span className="font-serif-italic cream">{t("territory.h1e")}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 1, delay: 0.12, ease: EASE }}
            className="mt-8 max-w-xl text-neutral-300 text-lg leading-[1.65]"
          >
            {t("territory.para")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 1, delay: 0.24, ease: EASE }}
            className="mt-10"
          >
            <StoreBadges />
          </motion.div>
        </div>
      </div>

      {/* Editorial photo strip — clipped frames */}
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 pb-24 sm:pb-32">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5">
          {plates.map((p, i) => (
            <motion.figure
              key={p.city}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }}
              className={`group relative${i === 0 ? " col-span-2 lg:col-span-1" : ""}`}
            >
              <div className="relative photo-frame overflow-hidden">
                <img
                  src={p.src}
                  alt={`${p.city}, ${p.region}`}
                  className="h-64 sm:h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              </div>
              <figcaption className="mt-4 flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.28em]">
                <span className="text-white">{p.city}</span>
                <span className="text-neutral-500">{p.region}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
